/** ONE RELATIONSHIPS — dialogs. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Modal, Field } from "../operations/StockInventoryDialogs";

const inputCls = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";

function ScopeSelect({ value, onChange, access, testid }) {
  const opts = ["BUSINESS", "SHARED", ...(access?.personal ? ["PERSONAL"] : [])];
  return (
    <Field label="Scope" required>
      <select value={value} onChange={onChange} data-testid={testid} className={inputCls}>
        {opts.map((s) => <option key={s} value={s}>{s}</option>)}
      </select></Field>);
}

function useConfig() {
  const [cfg, setCfg] = useState({});
  useEffect(() => {
    api.get("/relationships/config").then(({ data }) => setCfg(data.config || {}));
  }, []);
  return cfg;
}

async function save(path, item, payload, onDone) {
  try {
    if (item?.id) await api.patch(`${path}/${item.id}`, payload);
    else await api.post(path, payload);
    toast.success("Saved"); onDone();
  } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
}

const Footer = ({ onClose, onSave, testid }) => (
  <div className="flex justify-end gap-2 mt-4">
    <button type="button" onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
    <button type="button" onClick={onSave} data-testid={testid}
        className="text-[11px] px-3 py-1.5 rounded-md bg-rose-950 text-white">Save</button>
  </div>);

/** Search-and-pick from the unified directory (people/households/customers/vendors/clubs). */
export function EntityPicker({ onPick, testid }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!q.trim()) { setItems([]); return; }
    const t = setTimeout(() => {
      api.get("/relationships/directory", { params: { q } })
        .then(({ data }) => setItems((data.items || []).slice(0, 10)));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <input value={q} onChange={(e) => setQ(e.target.value)} data-testid={testid}
          placeholder="Search directory…" className={inputCls} />
      {items.length > 0 && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {items.map((it, i) => (
            <button key={i} type="button" data-testid={`${testid}-opt-${i}`}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation();
                  setQ(""); setItems([]); onPick(it); }}
                onClick={(e) => e.preventDefault()}
                className="w-full text-left text-[11px] px-2 py-1.5 hover:bg-slate-50 flex items-center gap-2">
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{it.type}</span>
              <span className="flex-1">{it.name}</span>
              <span className="text-slate-400">{it.detail || ""}</span>
            </button>))}
        </div>)}
    </div>);
}

// ------------------------------------------------------------------ PERSON
export function PersonDialog({ item, access, onClose, onDone }) {
  const cfg = useConfig();
  const [households, setHouseholds] = useState([]);
  const [f, setF] = useState({ scope: item.scope || "BUSINESS",
    display_name: item.display_name || "", relationship_category: item.relationship_category || "FRIEND",
    mobile: item.mobile || "", email: item.email || "", company: item.company || "",
    household_id: item.household_id || "", city: item.city || "",
    birthday: item.birthday || "", anniversary: item.anniversary || "",
    labels: item.labels || "", notes: item.notes || "" });
  useEffect(() => {
    api.get("/relationships/households").then(({ data }) => setHouseholds(data.items || []));
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title={item.id ? "Edit person" : "Add person"} onClose={onClose} wide>
      <div className="grid grid-cols-3 gap-3" data-testid="rel-person-dialog">
        <Field label="Full name" required>
          <input value={f.display_name} onChange={set("display_name")} data-testid="rel-pd-name" className={inputCls} /></Field>
        <Field label="Relationship" required>
          <select value={f.relationship_category} onChange={set("relationship_category")}
              data-testid="rel-pd-category" className={inputCls}>
            {(cfg.categories || []).map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select></Field>
        <ScopeSelect value={f.scope} onChange={set("scope")} access={access} testid="rel-pd-scope" />
        <Field label="Mobile">
          <input value={f.mobile} onChange={set("mobile")} data-testid="rel-pd-mobile" className={inputCls} /></Field>
        <Field label="Email">
          <input value={f.email} onChange={set("email")} className={inputCls} /></Field>
        <Field label="Company / organisation">
          <input value={f.company} onChange={set("company")} className={inputCls} /></Field>
        <Field label="Household">
          <select value={f.household_id} onChange={set("household_id")} data-testid="rel-pd-household" className={inputCls}>
            <option value="">— none —</option>
            {households.map((h) => <option key={h.id} value={h.id}>{h.display_name}</option>)}
          </select></Field>
        <Field label="Birthday">
          <input type="date" value={f.birthday} onChange={set("birthday")} data-testid="rel-pd-birthday" className={inputCls} /></Field>
        <Field label="Anniversary">
          <input type="date" value={f.anniversary} onChange={set("anniversary")} className={inputCls} /></Field>
        <Field label="City">
          <input value={f.city} onChange={set("city")} className={inputCls} /></Field>
        <Field label="Labels (comma separated)">
          <input value={f.labels} onChange={set("labels")} className={inputCls} /></Field>
        <Field label="Notes">
          <input value={f.notes} onChange={set("notes")} className={inputCls} /></Field>
      </div>
      <Footer onClose={onClose} testid="rel-pd-save"
          onSave={() => f.display_name ? save("/relationships/people", item, f, onDone) : toast.error("Name required")} />
    </Modal>);
}

// ------------------------------------------------------------------ HOUSEHOLD
export function HouseholdDialog({ item, access, onClose, onDone }) {
  const [f, setF] = useState({ scope: item.scope || "BUSINESS",
    display_name: item.display_name || "", preferred_gift_recipient: item.preferred_gift_recipient || "",
    city: item.city || "", notes: item.notes || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title={item.id ? "Edit household" : "Add household"} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3" data-testid="rel-household-dialog">
        <Field label="Household name" required>
          <input value={f.display_name} onChange={set("display_name")} data-testid="rel-hd-name"
              placeholder="e.g. Shah family — Mumbai" className={inputCls} /></Field>
        <ScopeSelect value={f.scope} onChange={set("scope")} access={access} testid="rel-hd-scope" />
        <Field label="Preferred gift recipient">
          <input value={f.preferred_gift_recipient} onChange={set("preferred_gift_recipient")}
              data-testid="rel-hd-recipient" className={inputCls} /></Field>
        <Field label="City">
          <input value={f.city} onChange={set("city")} className={inputCls} /></Field>
        <div className="col-span-2"><Field label="Notes">
          <input value={f.notes} onChange={set("notes")} className={inputCls} /></Field></div>
      </div>
      <Footer onClose={onClose} testid="rel-hd-save"
          onSave={() => f.display_name ? save("/relationships/households", item, f, onDone) : toast.error("Name required")} />
    </Modal>);
}

// ------------------------------------------------------------------ ORG
export function OrgDialog({ item, access, onClose, onDone }) {
  const cfg = useConfig();
  const [f, setF] = useState({ scope: item.scope || "BUSINESS", name: item.name || "",
    org_type: item.org_type || "CLUB", chapter: item.chapter || "",
    membership_number: item.membership_number || "", membership_type: item.membership_type || "",
    renewal_date: item.renewal_date || "", city: item.city || "",
    contact_person: item.contact_person || "", contact_mobile: item.contact_mobile || "",
    notes: item.notes || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title={item.id ? "Edit club / association" : "Add club / association"} onClose={onClose} wide>
      <div className="grid grid-cols-3 gap-3" data-testid="rel-org-dialog">
        <Field label="Name" required>
          <input value={f.name} onChange={set("name")} data-testid="rel-od-name" className={inputCls} /></Field>
        <Field label="Type" required>
          <select value={f.org_type} onChange={set("org_type")} data-testid="rel-od-type" className={inputCls}>
            {(cfg.org_types || []).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select></Field>
        <Field label="Chapter / branch">
          <input value={f.chapter} onChange={set("chapter")} className={inputCls} /></Field>
        <Field label="Membership number">
          <input value={f.membership_number} onChange={set("membership_number")}
              data-testid="rel-od-memno" className={inputCls} /></Field>
        <Field label="Membership type">
          <input value={f.membership_type} onChange={set("membership_type")}
              placeholder="e.g. Life member" className={inputCls} /></Field>
        <Field label="Renewal date">
          <input type="date" value={f.renewal_date} onChange={set("renewal_date")}
              data-testid="rel-od-renewal" className={inputCls} /></Field>
        <Field label="City">
          <input value={f.city} onChange={set("city")} className={inputCls} /></Field>
        <Field label="Contact person">
          <input value={f.contact_person} onChange={set("contact_person")} className={inputCls} /></Field>
        <Field label="Contact mobile">
          <input value={f.contact_mobile} onChange={set("contact_mobile")} className={inputCls} /></Field>
        <div className="col-span-3"><Field label="Notes">
          <input value={f.notes} onChange={set("notes")} className={inputCls} /></Field></div>
      </div>
      <Footer onClose={onClose} testid="rel-od-save"
          onSave={() => f.name ? save("/relationships/orgs", item, f, onDone) : toast.error("Name required")} />
    </Modal>);
}

// ------------------------------------------------------------------ ADDRESS
export function AddressDialog({ access, entity, item, onClose, onDone }) {
  const cfg = useConfig();
  const isEdit = !!item?.id;
  const [ent, setEnt] = useState(entity ||
    (isEdit ? { id: item.entity_id, type: item.entity_type, name: item.entity_name } : null));
  const [err, setErr] = useState("");
  const [f, setF] = useState({ scope: item?.scope || "BUSINESS",
    address_type: item?.address_type || "HOME", recipient_name: item?.recipient_name || "",
    line1: item?.line1 || "", line2: item?.line2 || "", city: item?.city || "",
    state: item?.state || "", postal_code: item?.postal_code || "",
    country: item?.country || "India", mobile: item?.mobile || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!ent) { setErr("Select who this address belongs to"); return toast.error("Pick who this address belongs to"); }
    if (!f.line1 || !f.city) return toast.error("Address line 1 and city are required");
    try {
      if (isEdit) {
        await api.patch(`/relationships/addresses/${item.id}`, f);
        toast.success("Address corrected");
      } else {
        await api.post("/relationships/addresses", { ...f, entity_id: ent.id,
          entity_type: ent.type, entity_name: ent.name,
          recipient_name: f.recipient_name || ent.name });
        toast.success("Address saved — previous address kept as history");
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Modal title={isEdit ? "Correct address" : "Add address"} onClose={onClose} wide>
      <div className="grid grid-cols-3 gap-3" data-testid="rel-address-dialog">
        <div className="col-span-2">
          <Field label="Belongs to" required>
            {ent ? (
              <div className="mt-1 flex items-center gap-2 text-[12px] border border-slate-200 rounded-md px-2 py-1.5"
                  data-testid="rel-ad-entity-chip">
                <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-slate-100">{ent.type}</span>
                <span className="flex-1">{ent.name}</span>
                {!isEdit && <button type="button" onClick={() => setEnt(null)}
                    className="text-[10px] text-rose-700">change</button>}
              </div>
            ) : <EntityPicker onPick={(it) => { setEnt(it); setErr(""); }} testid="rel-ad-entity" />}
            {err && !ent && <div className="text-[10px] text-rose-600 mt-1" data-testid="rel-ad-entity-error">{err}</div>}
          </Field>
        </div>
        <Field label="Address type" required>
          <select value={f.address_type} onChange={set("address_type")} data-testid="rel-ad-type" className={inputCls}>
            {(cfg.address_types || []).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select></Field>
        <Field label="Recipient name (for labels)">
          <input value={f.recipient_name} onChange={set("recipient_name")} className={inputCls} /></Field>
        <ScopeSelect value={f.scope} onChange={set("scope")} access={access} testid="rel-ad-scope" />
        <Field label="Mobile">
          <input value={f.mobile} onChange={set("mobile")} className={inputCls} /></Field>
        <div className="col-span-2"><Field label="Line 1" required>
          <input value={f.line1} onChange={set("line1")} data-testid="rel-ad-line1" className={inputCls} /></Field></div>
        <Field label="Line 2">
          <input value={f.line2} onChange={set("line2")} className={inputCls} /></Field>
        <Field label="City" required>
          <input value={f.city} onChange={set("city")} data-testid="rel-ad-city" className={inputCls} /></Field>
        <Field label="State">
          <input value={f.state} onChange={set("state")} className={inputCls} /></Field>
        <Field label="Postal code">
          <input value={f.postal_code} onChange={set("postal_code")} className={inputCls} /></Field>
      </div>
      <Footer onClose={onClose} onSave={submit} testid="rel-ad-save" />
    </Modal>);
}

// ------------------------------------------------------------------ CAMPAIGN
export function CampaignDialog({ item, access, onClose, onDone }) {
  const cfg = useConfig();
  const [f, setF] = useState({ scope: item.scope || "BUSINESS", name: item.name || "",
    occasion: item.occasion || "DIWALI", year: item.year || String(new Date().getFullYear()),
    gift: item.gift || "", budget: item.budget || "",
    status: item.status || "PLANNING", notes: item.notes || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title={item.id ? "Edit campaign" : "New gift campaign"} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3" data-testid="rel-campaign-dialog">
        <Field label="Campaign name" required>
          <input value={f.name} onChange={set("name")} data-testid="rel-cd-name"
              placeholder="e.g. Diwali 2026 — Sweet boxes" className={inputCls} /></Field>
        <Field label="Occasion" required>
          <select value={f.occasion} onChange={set("occasion")} data-testid="rel-cd-occasion" className={inputCls}>
            {(cfg.occasions || []).map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
          </select></Field>
        <Field label="Year">
          <input value={f.year} onChange={set("year")} className={inputCls} /></Field>
        <Field label="Status">
          <select value={f.status} onChange={set("status")} data-testid="rel-cd-status" className={inputCls}>
            {["PLANNING", "READY", "DISPATCH_IN_PROGRESS", "COMPLETED", "CANCELLED"].map((s) =>
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select></Field>
        <Field label="Default gift">
          <input value={f.gift} onChange={set("gift")} data-testid="rel-cd-gift"
              placeholder="e.g. Dry fruit box" className={inputCls} /></Field>
        <Field label="Budget per gift (₹)">
          <input value={f.budget} onChange={set("budget")} className={inputCls} /></Field>
        <ScopeSelect value={f.scope} onChange={set("scope")} access={access} testid="rel-cd-scope" />
        <Field label="Notes">
          <input value={f.notes} onChange={set("notes")} className={inputCls} /></Field>
      </div>
      <Footer onClose={onClose} testid="rel-cd-save"
          onSave={() => f.name ? save("/relationships/campaigns", item, f, onDone) : toast.error("Name required")} />
    </Modal>);
}

// ------------------------------------------------------------------ RECIPIENT (with duplicate guard)
export function RecipientDialog({ campaign, onClose, onDone }) {
  const [ent, setEnt] = useState(null);
  const [warn, setWarn] = useState(null);
  const [f, setF] = useState({ gift: campaign.gift || "", quantity: 1, address: "",
    city: "", state: "", postal_code: "", mobile: "", notes: "", override_reason: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pick = async (it) => {
    setEnt(it); setWarn(null);
    setF((p) => ({ ...p, mobile: it.mobile || p.mobile }));
    try {
      const { data } = await api.get("/relationships/addresses", { params: { entity_id: it.id } });
      const cur = (data.items || []).find((a) => a.current);
      if (cur) setF((p) => ({ ...p, address: [cur.line1, cur.line2].filter(Boolean).join(", "),
        city: cur.city || "", state: cur.state || "", postal_code: cur.postal_code || "",
        mobile: cur.mobile || p.mobile }));
    } catch { /* no address yet */ }
  };
  const submit = async () => {
    if (!ent) return toast.error("Pick a recipient from the directory");
    try {
      const payload = { ...f, entity_id: ent.id, entity_type: ent.type,
        recipient_name: ent.name, household_id: ent.household_id || null,
        household_name: ent.type === "HOUSEHOLD" ? ent.name : undefined };
      if (!payload.override_reason) delete payload.override_reason;
      const { data } = await api.post(`/relationships/campaigns/${campaign.id}/recipients`, payload);
      if (data.blocked) { setWarn(data); return; }
      if (data.last_gift) toast.info(`Note: last delivered gift was "${data.last_gift.gift || "—"}"`);
      toast.success("Recipient added"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title={`Add recipient — ${campaign.name}`} onClose={onClose} wide>
      <div className="grid grid-cols-3 gap-3" data-testid="rel-recipient-dialog">
        <div className="col-span-3">
          <Field label="Recipient (person, household, customer, vendor or club)" required>
            {ent ? (
              <div className="mt-1 flex items-center gap-2 text-[12px] border border-slate-200 rounded-md px-2 py-1.5"
                  data-testid="rel-rd-entity-chip">
                <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-slate-100">{ent.type}</span>
                <span className="flex-1">{ent.name}</span>
                <button type="button" onClick={() => { setEnt(null); setWarn(null); }}
                    className="text-[10px] text-rose-700">change</button>
              </div>
            ) : <EntityPicker onPick={pick} testid="rel-rd-entity" />}
          </Field>
        </div>
        <Field label="Gift">
          <input value={f.gift} onChange={set("gift")} data-testid="rel-rd-gift" className={inputCls} /></Field>
        <Field label="Quantity">
          <input type="number" min={1} value={f.quantity} onChange={set("quantity")} className={inputCls} /></Field>
        <Field label="Mobile">
          <input value={f.mobile} onChange={set("mobile")} className={inputCls} /></Field>
        <div className="col-span-3"><Field label="Delivery address">
          <input value={f.address} onChange={set("address")} data-testid="rel-rd-address" className={inputCls} /></Field></div>
        <Field label="City">
          <input value={f.city} onChange={set("city")} className={inputCls} /></Field>
        <Field label="State">
          <input value={f.state} onChange={set("state")} className={inputCls} /></Field>
        <Field label="Postal code">
          <input value={f.postal_code} onChange={set("postal_code")} className={inputCls} /></Field>
        <div className="col-span-3"><Field label="Notes">
          <input value={f.notes} onChange={set("notes")} className={inputCls} /></Field></div>
        {warn && (
          <div className="col-span-3 border border-amber-300 bg-amber-50 rounded-lg p-3 space-y-2"
              data-testid="rel-rd-dup-warning">
            <div className="text-[11px] font-semibold text-amber-900">
              ⚠ {warn.duplicate_status === "ALREADY_INCLUDED"
                ? "This recipient is already in the campaign."
                : "POSSIBLE DUPLICATE — someone from the same household / mobile is already in this campaign."}
            </div>
            <div className="text-[10.5px] text-amber-800">To include anyway, give a reason (recorded in history):</div>
            <input value={f.override_reason} onChange={set("override_reason")}
                data-testid="rel-rd-override" placeholder="Override reason…" className={inputCls} />
          </div>)}
      </div>
      <Footer onClose={onClose} onSave={submit} testid="rel-rd-save" />
    </Modal>);
}

// ------------------------------------------------------------------ GIFT / GROUP
export function GiftDialog({ item, access, onClose, onDone }) {
  const cfg = useConfig();
  const [f, setF] = useState({ scope: item.scope || "BUSINESS", name: item.name || "",
    gift_type: item.gift_type || "SWEET_BOX", budget: item.budget || "",
    vendor: item.vendor || "", notes: item.notes || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title={item.id ? "Edit gift" : "Add gift to catalog"} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3" data-testid="rel-gift-dialog">
        <Field label="Gift name" required>
          <input value={f.name} onChange={set("name")} data-testid="rel-gd-name" className={inputCls} /></Field>
        <Field label="Type">
          <select value={f.gift_type} onChange={set("gift_type")} className={inputCls}>
            {(cfg.gift_types || []).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select></Field>
        <Field label="Budget (₹)">
          <input value={f.budget} onChange={set("budget")} className={inputCls} /></Field>
        <Field label="Vendor / source">
          <input value={f.vendor} onChange={set("vendor")} className={inputCls} /></Field>
        <div className="col-span-2"><Field label="Notes">
          <input value={f.notes} onChange={set("notes")} className={inputCls} /></Field></div>
      </div>
      <Footer onClose={onClose} testid="rel-gd-save"
          onSave={() => f.name ? save("/relationships/gifts", item, f, onDone) : toast.error("Name required")} />
    </Modal>);
}

export function GroupDialog({ item, access, onClose, onDone }) {
  const [f, setF] = useState({ scope: item.scope || "BUSINESS",
    name: item.name || "", description: item.description || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title={item.id ? "Edit label" : "Add label"} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3" data-testid="rel-group-dialog">
        <Field label="Label name" required>
          <input value={f.name} onChange={set("name")} data-testid="rel-grd-name"
              placeholder="e.g. Diwali A-list" className={inputCls} /></Field>
        <ScopeSelect value={f.scope} onChange={set("scope")} access={access} testid="rel-grd-scope" />
        <div className="col-span-2"><Field label="Description">
          <input value={f.description} onChange={set("description")} className={inputCls} /></Field></div>
      </div>
      <Footer onClose={onClose} testid="rel-grd-save"
          onSave={() => f.name ? save("/relationships/groups", item, f, onDone) : toast.error("Name required")} />
    </Modal>);
}
